import { connect, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { Route } from 'react-router-dom';
import { withRouter } from 'react-router';
import includes from 'lodash/includes';
import { TokenSenderContext } from '@tymate/sergic';
import { Badge, List, media, NavLink, spacing } from 'ui';
import { SidebarMenu, SidebarMenuItem, SidebarMenuLink } from 'ui/sidebar';
import IcCommunity from 'components/icons/Community';
import IcSettings from 'components/icons/Settings';
import { SERGIC_OFFER, SO_GENERAL_MEETING_WEBAPP_URL } from '../constants';
import syndicOne from 'images/logo-syndic-one-full.svg';
import viva from 'images/viva-logo-white.svg';
import eseis from 'images/eseis-logo-white.svg';
import ecopro from 'images/logo-ecopro-full.svg';
import neuf from 'images/sergic-neuf-logo-white.svg';
import tradition from 'images/logo-tradition-padded.svg';
import { getSelectedPlace, selectCurrentRole } from 'selectors';
import {
   allowedToAccessAccounts,
   allowedToAccessBillers,
   allowedToAccessConversations,
   allowedToAccessDocuments,
   allowedToAccessExpenses,
   allowedToAccessForum,
   allowedToAccessOwnAccounts,
   allowedToAccessPublicAccounts,
   allowedToAccessReports,
   allowedToAccessSupport,
} from 'utils/roles';
import PlaceSwitcher from 'components/PlaceSwitcher';
import { getAppName } from 'components/AppBanner';
import { withTranslation } from 'react-i18next';
import { IcDocument } from '@tymate/sergic/components/icons';
import IcCommunication from 'components/icons/Communication.js';
import IcHome from './icons/Home';
import IcAccount from './icons/Account';
import IconDocument from './icons/IcDocument';
import { useBreakpoint } from '../hooks';
import capitalize from 'lodash/capitalize';

const Wrapper = styled.aside`
   background-color: #fff;
   position: fixed;
   bottom: 0;
   left: 0;
   right: 0;
   z-index: 1;
   border-top: 1px solid ${({ theme }) => theme.separator};
   box-shadow: inset -1px 0 0 ${({ theme }) => theme.separator};

   @supports (padding: max(0px)) {
      padding-bottom: max(0px, env(safe-area-inset-bottom));
   }

   ${media.tablet`
    background-color: ${({ theme }) => theme.primary};
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 250px;
    padding-bottom: ${16 + 80}px;
    border-top: 0;

    @supports (padding: max(0px)) {
      padding-left: max(0px, env(safe-area-inset-left));
    }
  `};
`;

const Logo = styled.span`
   display: none;

   ${media.tablet`
    display: block;
    
  `};
`;

const LogoImg = styled.img`
   display: block;
   max-width: 100%;
   position: absolute;
   bottom: 0;
   margin: 2rem;

   ${props =>
      props.offer === 'VQC' &&
      css`
         padding: 0 ${({ theme }) => theme.spacing()} ${({ theme }) => theme.spacing()};
      `}
`;

const HeaderChunk = styled.div`
   vertical-align: middle;

   ${props =>
      props.main &&
      css`
         width: 100%;
      `};
`;

const NavList = styled(List)`
   display: flex;
   font-size: 10px;
   justify-content: space-between;

   ${media.tablet`
    display: block;
    font-size: inherit;
  `};
`;

const NavListItem = styled.li`
   vertical-align: middle;
   position: relative;
   flex: 1;

   ${props =>
      props.isHiddenOnDesktop &&
      css`
         ${media.tablet`display: none;`};
      `};

   ${props =>
      props.shouldBeHiddenOnSmallViewports &&
      css`
         display: none;

         ${media.tablet`
          display: block;
        `};
      `};
`;

const NavListItemLink = styled(NavLink)`
   text-decoration: none;
   padding: ${spacing(0.5)} ${spacing(0.25)} ${spacing(0.25)};
   display: block;
   display: flex;
   align-items: center;
   display: flex;
   flex-wrap: wrap;
   color: inherit;
   text-align: center;
   font-weight: 500;

   svg {
      margin-right: auto;
      margin-left: auto;
   }

   > span {
      display: none;

      + span {
         flex: 0 0 100%;
         display: block;
         margin-top: ${spacing(0.25)};
      }
   }

   &.active {
      color: ${({ theme }) => theme.primary};
   }

   ${media.tablet`
    transition: background-color 150ms ease, opacity 150ms ease;
    display: block;
    font-weight: 600;
    color: ${({ theme }) => theme.sidebarLinkColor};
    text-align: left;
    padding: ${({ theme }) => theme.sidebarLinkPadding};
    margin: ${({ theme }) => theme.sidebarLinkMargin};
    border-radius: ${({ theme }) => theme.sidebarLinkBorderRadius};

    svg {
      margin-right: ${spacing(0.5)};
      margin-left: 0;
    }

    span {
      display: inline;

      + span {
        display: none;
      }
    }

    &.active {
      opacity: 1;
      svg {
        color: ${({ theme }) => theme.sidebarActiveLinkSvgColor}
      }
    }

    &.active {
      color: ${({ theme }) => theme.sidebarActiveLinkColor};
      background-color: ${({ theme }) => theme.sidebarActiveLinkBackgroundColor};
    }
  `};
`;

const AlertIcon = styled.div`
   position: relative;
   margin-right: ${({ theme }) => theme.spacing()};
`;

function Header(props) {
   const getLogoURL = () => {
      switch (SERGIC_OFFER) {
         case 'DIR':
            return syndicOne;
         case 'VSY':
            return viva;
         case 'ESE':
            return eseis;
         case 'ECO':
            return ecopro;
         case 'VQC':
            return tradition;
         default:
            return neuf;
      }
   };

   const { role, isImpersonating, unreadBillerEntriesCount, t } = props;

   const place = useSelector(getSelectedPlace);

   const communicationSidebarItems = [
      {
         label: t('communications:support'),
         pathname: 'support',
         allowedFor: allowedToAccessSupport,
      },
      {
         label: t('communications:myMessages'),
         pathname: 'mes-messages',
         allowedFor: isImpersonating ? [] : allowedToAccessConversations,
      },
      {
         label: SERGIC_OFFER === 'DIR' ? t('communications:buildingForum') : t('communications:syndicForum'),
         pathname: 'forum',
         allowedFor: allowedToAccessForum,
      },
      {
         label: capitalize(t('reports:reports')),
         pathname: 'signalements',
         allowedFor: allowedToAccessReports,
      },
      {
         label: t('communications:contacts'),
         pathname: 'contacts',
      },
   ];

   const accountsSidebarItems = [
      {
         label: t('account:personals'),
         pathname: 'personnels',
         allowedFor: allowedToAccessOwnAccounts,
      },
      {
         label: t('account:building'),
         pathname: 'immeuble',
         allowedFor: !place.canViewInfo ? [] : allowedToAccessPublicAccounts,
         unread: place.canManageExpenses && includes(allowedToAccessExpenses, role) ? unreadBillerEntriesCount : 0,
      },
      {
         label: t('account:budgets'),
         pathname: 'immeuble/budget',
         allowedFor: ['tenant'],
      },
      {
         label: t('account:billers'),
         pathname: 'facturiers',
         allowedFor: !place.canViewInfo ? [] : allowedToAccessBillers,
      },
   ];

   const documentsSidebarItems = [
      {
         label: t('account:personals'),
         pathname: 'personnels',
      },
      {
         label: t('account:building'),
         pathname: 'immeuble',
      },
   ];

   const { isMobile } = useBreakpoint();

   return (
      <Wrapper>
         <PlaceSwitcher withPlaceImage />

         <HeaderChunk main>
            <NavList role='navigation'>
               <NavListItem>
                  <NavListItemLink to='/' exact>
                     <IcHome size={isMobile ? 24 : 30} />
                     <span>{t('misc:home')}</span>
                     <span>{t('misc:home')}</span>
                  </NavListItemLink>
               </NavListItem>

               <NavListItem>
                  <NavListItemLink to='/mes-echanges'>
                     <IcCommunication size={isMobile ? 24 : 30} />
                     <span>{t('communications:myCommunications')}</span>
                     <span>{t('communications:communications')}</span>
                  </NavListItemLink>
                  <Route
                     path='/mes-echanges'
                     render={() => (
                        <SidebarMenu dark>
                           {communicationSidebarItems
                              .filter(({ allowedFor }) => !allowedFor || includes(allowedFor, role))
                              .map(({ label, pathname, icon }) => (
                                 <SidebarMenuItem key={label}>
                                    <SidebarMenuLink to={`/mes-echanges/${pathname}`}>
                                       <span>{label}</span>
                                    </SidebarMenuLink>
                                 </SidebarMenuItem>
                              ))}
                        </SidebarMenu>
                     )}
                  />
               </NavListItem>

               {allowedToAccessAccounts.indexOf(role) > -1 && (
                  <NavListItem>
                     <NavListItemLink to='/mes-comptes'>
                        <IcAccount size={isMobile ? 24 : 30} />
                        <span>{t('account:myAccounts')}</span>
                        <span>{t('account:accounts')}</span>
                     </NavListItemLink>
                     <Route
                        path='/mes-comptes'
                        render={() => (
                           <SidebarMenu dark>
                              {accountsSidebarItems
                                 .filter(({ allowedFor }) => !allowedFor || includes(allowedFor, role))
                                 .map(({ label, pathname, unread }) => (
                                    <SidebarMenuItem key={label}>
                                       <SidebarMenuLink to={`/mes-comptes/${pathname}`}>
                                          <span>{label}</span>
                                          {Boolean(unread) && unread > 0 && (
                                             <AlertIcon>
                                                <IcDocument />
                                                <Badge>{unread}</Badge>
                                             </AlertIcon>
                                          )}
                                       </SidebarMenuLink>
                                    </SidebarMenuItem>
                                 ))}
                           </SidebarMenu>
                        )}
                     />
                  </NavListItem>
               )}

               {allowedToAccessDocuments.indexOf(role) > -1 && (
                  <NavListItem>
                     <NavListItemLink to='/mes-documents'>
                        <IconDocument size={isMobile ? 24 : 30} />
                        <span>{t('documents:myDocuments')}</span>
                        <span>{t('documents:documents')}</span>
                     </NavListItemLink>
                     <Route
                        path='/mes-documents'
                        render={() => (
                           <SidebarMenu dark>
                              {documentsSidebarItems.map(({ label, pathname }) => (
                                 <SidebarMenuItem key={label}>
                                    <SidebarMenuLink to={`/mes-documents/${pathname}`}>
                                       <span>{label}</span>
                                    </SidebarMenuLink>
                                 </SidebarMenuItem>
                              ))}
                           </SidebarMenu>
                        )}
                     />
                  </NavListItem>
               )}

               {SERGIC_OFFER === 'DIR' && (
                  <NavListItem shouldBeHiddenOnSmallViewports>
                     <TokenSenderContext.Consumer>
                        {({ onTokenLogin }) => (
                           <NavListItemLink
                              as='a'
                              onClick={async e => {
                                 e.preventDefault();
                                 await onTokenLogin();
                                 window.location = SO_GENERAL_MEETING_WEBAPP_URL;
                              }}
                              href={`${SO_GENERAL_MEETING_WEBAPP_URL}/connexion`}
                           >
                              <IcCommunity size={24} style={{ marginRight: spacing(0.5) }} />
                              <span>{t('community:generalAssembly')}</span>
                           </NavListItemLink>
                        )}
                     </TokenSenderContext.Consumer>
                  </NavListItem>
               )}

               <NavListItem isHiddenOnDesktop>
                  <NavListItemLink to='/parametres'>
                     <IcSettings size={24} />
                     <span>{t('misc:settings')}</span>
                     <span>{t('misc:settings')}</span>
                  </NavListItemLink>
                  <Route
                     path='/parametres'
                     render={() => (
                        <SidebarMenu dark>
                           {[]
                              .filter(({ offers }) => !offers || includes(offers, SERGIC_OFFER))
                              .filter(({ allowedFor }) => includes(allowedFor, role))
                              .map(({ label, pathname }) => (
                                 <SidebarMenuItem key={label}>
                                    <SidebarMenuLink to={`/parametres/${pathname}`}>
                                       <span>{label}</span>
                                    </SidebarMenuLink>
                                 </SidebarMenuItem>
                              ))}
                        </SidebarMenu>
                     )}
                  />
               </NavListItem>
            </NavList>
         </HeaderChunk>

         <Logo>
            <LogoImg offer={SERGIC_OFFER} src={getLogoURL()} alt={getAppName()} />
         </Logo>
      </Wrapper>
   );
}

const mapStateToProps = state => ({
   user: state.user,
   offer: state.ui.offer,
   role: selectCurrentRole(state),
   place: getSelectedPlace(state),
   isImpersonating: Boolean(state.user.impersonatedUser),
   unreadEntriesCount: state.expenses.summary.unreadEntriesCount,
});

export default withRouter(connect(mapStateToProps)(withTranslation()(Header)));
